import { useAdaptivePolling } from "@/hooks/use-adaptive-polling";
import { useUser } from "@/providers/user";
import { LAYOUT_AUDIENCES } from "@/lib/navigation";
import { DataService } from "@/services/data";
import { SFEvent } from "@/types/salesforce/event";
import { DateTime } from "luxon";
import { useCallback, useRef, useState } from "react";

// Clé pour le localStorage
const CLICKED_EVENTS_KEY = "walter_crm_clicked_events";

// Fonctions utilitaires pour gérer le localStorage
const getClickedEvents = (): string[] => {
    try {
        const stored = localStorage.getItem(CLICKED_EVENTS_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.warn("Erreur lors de la lecture des événements cliqués:", error);
        return [];
    }
};

const addClickedEvent = (eventId: string): void => {
    try {
        const clickedEvents = getClickedEvents();
        if (!clickedEvents.includes(eventId)) {
            const updatedEvents = [...clickedEvents, eventId];
            localStorage.setItem(CLICKED_EVENTS_KEY, JSON.stringify(updatedEvents));
        }
    } catch (error) {
        console.warn("Erreur lors de l'ajout de l'événement cliqué:", error);
    }
};

const clearClickedEvents = (): void => {
    try {
        localStorage.removeItem(CLICKED_EVENTS_KEY);
    } catch (error) {
        console.warn("Erreur lors de la suppression des événements cliqués:", error);
    }
};

export function useEventAlerts() {
    const { user: currentUser, is_visible_to_audience } = useUser();
    // Calendar reminders are not offered to every audience (partners have no calendar): no polling then.
    const user = is_visible_to_audience(LAYOUT_AUDIENCES.eventAlerts) ? currentUser : null;
    const [alerts, setAlerts] = useState<SFEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const alertsRef = useRef<SFEvent[]>([]);
    const loadingControllerRef = useRef<AbortController | null>(null);

    // Mettre à jour la référence pour éviter les closures
    alertsRef.current = alerts;

    // Fonction pour récupérer les événements du calendrier
    const fetchCalendarEvents = useCallback(async (controller: AbortController) => {
        if (!user) return [];

        try {
            loadingControllerRef.current = controller;
            setIsLoading(true);

            // Récupérer les événements pour l'utilisateur actuel
            const response = await DataService.client
                .url("/crm/objects/events/")
                .query({
                    owner_id: user.id.toString(),
                    date__range: `${DateTime.now().toISODate()},${DateTime.now().plus({ days: 1 }).toISODate()}`,
                    is_cancelled: "false",
                    is_reminder_set: "true",
                    page_size: "100"
                })
                .signal(controller)
                .get()
                .json();

            const events = (response as any).results || [];
            return events;
        } catch (error) {
            if (!controller.signal.aborted) {
                console.error("Erreur lors de la récupération des événements:", error);
            }
            throw error;
        } finally {
            if (loadingControllerRef.current === controller) {
                loadingControllerRef.current = null;
                setIsLoading(false);
            }
        }
    }, [user]);

    // Fonction pour créer une alerte à partir d'un événement
    const createAlertFromEvent = useCallback((event: any): SFEvent | null => {
        const now = DateTime.now();

        // Vérifier d'abord si l'événement n'est pas trop ancien (start_datetime dépassée depuis plus de 15min)
        if (event.start_datetime) {
            const startTime = DateTime.fromISO(event.start_datetime);
            const timeSinceStart = now.diff(startTime, "minutes").minutes;
            if (timeSinceStart > 15) {
                return null; // Événement trop ancien
            }
        }

        // Utiliser reminder_datetime si disponible, sinon start_datetime
        const reminderTime = event.reminder_datetime || event.start_datetime;
        if (!reminderTime) return null;

        const reminderDateTime = DateTime.fromISO(reminderTime);
        const timeUntilReminder = reminderDateTime.diff(now, "minutes").minutes;

        // Afficher l'alerte si le rappel est dépassé OU dans les 5 prochaines minutes

        if (timeUntilReminder > 0) return null;

        return {
            sf_id: event.sf_id,
            start_datetime: event.start_datetime,
            reminder_datetime: event.reminder_datetime,
            ...event
        };
    }, []);

    // Fonction pour vérifier et mettre à jour les alertes
    const checkAndUpdateAlerts = useCallback(async (controller = new AbortController()) => {
        const events = await fetchCalendarEvents(controller);
        if (!events.length) return;

        const now = DateTime.now();
        const newAlerts: SFEvent[] = [];
        const clickedEvents = getClickedEvents();

        // Créer des alertes pour les événements à venir
        for (const event of events) {
            const alert = createAlertFromEvent(event);
            if (alert) {
                // Vérifier si l'événement n'a pas déjà été cliqué
                const eventId = alert.sf_id || alert.id.toString();
                if (!clickedEvents.includes(eventId)) {
                    newAlerts.push(alert);
                }
            }
        }

        // Filtrer les alertes existantes qui ne sont plus valides (start_datetime dépassée depuis plus de 15min)
        const validAlerts = alertsRef.current.filter(alert => {
            // Vérifier si l'événement n'est pas trop ancien (start_datetime dépassée depuis plus de 15min)
            if (alert.start_datetime) {
                const startTime = DateTime.fromISO(alert.start_datetime);
                const timeSinceStart = now.diff(startTime, "minutes").minutes;
                if (timeSinceStart > 15) {
                    return false; // Événement trop ancien
                }
            }
            return true; // Garder l'alerte si l'événement n'est pas trop ancien
        });

        // Ajouter les nouvelles alertes
        const allAlerts = [...validAlerts, ...newAlerts];

        // Supprimer les doublons
        const uniqueAlerts = allAlerts.filter((alert, index, self) => index === self.findIndex(a => a.sf_id === alert.sf_id));

        setAlerts(uniqueAlerts);
    }, [fetchCalendarEvents, createAlertFromEvent]);

    // Fonction pour supprimer une alerte
    const removeAlert = useCallback((alertId: string) => {
        // Ajouter l'ID de l'événement aux événements cliqués
        addClickedEvent(alertId);

        // Supprimer l'alerte de la liste
        setAlerts(prev => prev.filter(alert => (alert.sf_id || alert.id.toString()) !== alertId));
    }, []);

    // Fonction pour supprimer toutes les alertes
    const clearAllAlerts = useCallback(() => {
        setAlerts([]);
    }, []);

    useAdaptivePolling(checkAndUpdateAlerts, {
        intervalMs: 30_000,
        maxBackoffMs: 180_000,
        enabled: Boolean(user),
        refreshKey: user?.id
    });

    return {
        alerts,
        isLoading,
        removeAlert,
        clearAllAlerts,
        refreshAlerts: checkAndUpdateAlerts,
        clearClickedEvents
    };
}
